import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Info, RefreshCw } from 'lucide-react'
import Joyride, { CallBackProps, STATUS } from 'react-joyride'
import { useLanguage } from '@/context/language-context'
import { useShop } from '@/context/shop-context'
import { useTranslation } from '@/hooks/use-translation'
import { buildMenuUrl } from '@/lib/build-menu-url'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { PageLayout } from '@/components/layout/page-layout'
import { ActiveUsersAnalyticsCard } from './components/active-users-analytic-card'
import { DashboardAnalyticsCards } from './components/dashboard-analytics-cards'
import { PopularItemsAnalyticsCard } from './components/popular-items-analytics-card'
import { WeeklyVisitOverview } from './components/weekly-visit-overview'

export default function Dashboard() {
  const { t } = useTranslation()
  const [runTour, setRunTour] = useState(false)
  const primaryColor = '#D92B04'
  const queryClient = useQueryClient()
  const { activeShop } = useShop()
  const { language } = useLanguage()
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null)
  const [, setUpdateTrigger] = useState(0)
  const [overviewTooltipOpen, setOverviewTooltipOpen] = useState(false)

  useEffect(() => {
    if (localStorage.getItem('dashboard-tour') !== 'done') {
      setRunTour(true)
    }
  }, [])

  // Update button state and last refreshed text periodically
  useEffect(() => {
    if (!lastRefreshTime) return

    const interval = setInterval(() => {
      // Force re-render to update button disabled state and last refreshed text
      setUpdateTrigger((prev) => prev + 1)
    }, 1000) // Update every second

    return () => clearInterval(interval)
  }, [lastRefreshTime])

  const isButtonDisabled = () => {
    if (!lastRefreshTime) return false
    const secondsSinceRefresh = Math.floor(
      (Date.now() - lastRefreshTime) / 1000
    )
    return secondsSinceRefresh < 180 // 3 minutes = 180 seconds
  }

  const handleRefresh = () => {
    if (isButtonDisabled() || !activeShop?._id) return

    // Invalidate all analytics queries
    queryClient.invalidateQueries({
      queryKey: ['analytics.shop.stats', activeShop._id],
    })
    queryClient.invalidateQueries({
      queryKey: ['analytics.shop.activeUsers', activeShop._id],
    })
    queryClient.invalidateQueries({
      queryKey: ['analytics.shop.weekly', activeShop._id],
    })
    queryClient.invalidateQueries({
      queryKey: ['analytics.shop.menu.stat', activeShop._id],
    })

    // Set last refresh time
    setLastRefreshTime(Date.now())
  }

  const getLastRefreshText = () => {
    if (!lastRefreshTime) return null

    const secondsAgo = Math.floor((Date.now() - lastRefreshTime) / 1000)
    const minutesAgo = Math.floor(secondsAgo / 60)

    if (minutesAgo === 0) {
      return t('Just now')
    } else if (minutesAgo === 1) {
      return t('1 minute ago')
    } else {
      return `${minutesAgo} ${t('minutes ago')}`
    }
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const finished = [STATUS.FINISHED, STATUS.SKIPPED]
    if (finished.some((finishedStatus) => finishedStatus === data.status)) {
      localStorage.setItem('dashboard-tour', 'done')
      setRunTour(false)
    }
  }

  const handleGoToMenu = () => {
    if (!activeShop?.slug) return

    const menuUrl = buildMenuUrl(language, activeShop.slug)
    window.open(menuUrl, '_blank')
  }

  const handleOpenMenuEditor = () => {
    window.location.href = '/menu-editor'
  }

  return (
    <PageLayout
      title={t('Dashboard')}
      description={t('Monitor your restaurant performance and menu activity.')}
      actions={
        <>
          {lastRefreshTime && (
            <span className='text-muted-foreground text-sm'>
              {getLastRefreshText()}
            </span>
          )}
          <Button
            onClick={handleRefresh}
            disabled={isButtonDisabled()}
            variant='outline'
            size='sm'
            className='gap-2'
          >
            <RefreshCw className='h-4 w-4' />
            {t('Refresh Data')}
          </Button>
        </>
      }
    >
      <Joyride
        steps={[
          {
            target: '.daily-stats-section',
            content: t('You can see your daily stats here.'),
          },
          {
            target: '.weekly-stats-section',
            content: t('You can see weekly stats here to compare weekdays.'),
          },
          {
            target: '.popular-items-section',
            content: t(
              'Check which items get the most views and compare them.'
            ),
          },
        ]}
        run={runTour}
        callback={handleJoyrideCallback}
        continuous
        showProgress
        showSkipButton
        styles={{ options: { primaryColor: primaryColor } }}
      />
      <div className='space-y-4'>
        {activeShop && (
          <Card className='quick-links-section'>
            <CardHeader>
              <CardTitle>{t('Quick Links')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='flex gap-3'>
                {activeShop?.slug && (
                  <Button onClick={handleGoToMenu} variant='outline'>
                    {t('Go to menu')}
                  </Button>
                )}
                <Button onClick={handleOpenMenuEditor} variant='outline'>
                  {t('Open Menu Editor')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <div className='daily-stats-section grid gap-4 sm:grid-cols-2 lg:grid-cols-5'>
          <ActiveUsersAnalyticsCard />
          <DashboardAnalyticsCards />
        </div>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-7'>
          <Card className='weekly-stats-section col-span-1 lg:col-span-4'>
            <CardHeader className='flex flex-row items-center justify-between'>
              <CardTitle>{t('Overview')}</CardTitle>
              <TooltipProvider delayDuration={0}>
                <Tooltip
                  open={overviewTooltipOpen}
                  onOpenChange={setOverviewTooltipOpen}
                >
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 p-0'
                      onClick={() =>
                        setOverviewTooltipOpen(!overviewTooltipOpen)
                      }
                    >
                      <Info className='text-muted-foreground h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='top'>
                    {t('Traffic trends for the past week.')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className='pl-2'>
              <WeeklyVisitOverview />
            </CardContent>
          </Card>
          <PopularItemsAnalyticsCard />
        </div>
      </div>
    </PageLayout>
  )
}
